<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import canvasHelper from './canvasHelper.ts'
import { useStore } from '@/stores/store.ts'
import type { Stage } from 'konva/lib/Stage'
import type { Layer } from 'konva/lib/Layer'
import { storeToRefs } from 'pinia'

const store = useStore()
const {
  buttonsCols,
  buttonsRows,
  isStitches,
  isButtons,
  stitchesCols,
  stitchesRows,
  isButtonMiddle,
  buttonsDistribution,
  buttonsDistanceToEdge,
  crossTopSewing,
  diameter_a_circle,
  thickness,
} = storeToRefs(store)

const strokeWidth = 1
const innerCircleRadius = 5
const outerSquareWitdh = innerCircleRadius * 4
const stage = ref<Stage|null>(null)

const commonTextSettings = canvasHelper.getCommonTextSettings()

const { width, height } = defineProps<{
  width: number
  height: number
}>()

const emit = defineEmits(['onformdraw'])

const leftPadding = 45
const bottomPadding = 25

onMounted(() => {
  const stageRef = stage.value?.getStage()
  const frameLayer = new window.Konva.Layer()
  const lineLayer = new window.Konva.Layer()
  const circleLayer = new window.Konva.Layer()
  stageRef?.add(frameLayer)
  stageRef?.add(lineLayer)
  stageRef?.add(circleLayer)
  drawFrame(frameLayer)
  drawForm(lineLayer, circleLayer)

  watch(
    [
      buttonsCols,
      buttonsRows,
      isStitches,
      isButtons,
      stitchesCols,
      stitchesRows,
      isButtonMiddle,
      buttonsDistribution,
      buttonsDistanceToEdge,
      crossTopSewing,
    ],
    () => {
      drawForm(lineLayer, circleLayer)
    },
  )

  watch([thickness, diameter_a_circle], () => {
    drawFrame(frameLayer)
    drawForm(lineLayer, circleLayer)
  })
})
function drawFrame(frameLayer: Layer) {
  const totalShapeWidth = width - leftPadding
  const totalShapeHeight = height - bottomPadding
  frameLayer.removeChildren()
  const radius = totalShapeWidth > totalShapeHeight ? totalShapeHeight / 3 : totalShapeWidth / 3

  const shapeWrapper = new window.Konva.Circle({
    x: totalShapeWidth / 2 + leftPadding / 2,
    y: totalShapeHeight / 2 - bottomPadding / 2,
    radius: radius,
    stroke: '#00000033',
    strokeWidth: strokeWidth,
  })
  //add shape into layer
  frameLayer.add(shapeWrapper)

  const thicknessRectangle = new window.Konva.Rect({
    x: leftPadding / 2,
    y: totalShapeHeight - bottomPadding,
    width: totalShapeWidth,
    height: thickness.value * 5,
    stroke: '#00000033',
    strokeWidth: strokeWidth,
  })

  frameLayer.add(thicknessRectangle)

    const text = new window.Konva.Text({
      ...commonTextSettings,
      text: thickness.value + ' cm',
      x: totalShapeWidth / 2,
      y:totalShapeHeight - bottomPadding - 5 + (thickness.value * 5) / 2,
    })

    frameLayer.add(text)

  const diameter_a_circle_text = new window.Konva.Text({
    ...commonTextSettings,
    text: diameter_a_circle.value + ' cm',
    x: totalShapeWidth / 2 + 5,
    y: totalShapeHeight / 2 - leftPadding,
  })

  frameLayer.add(diameter_a_circle_text)
  const innerShapeHalfHeight = Math.sqrt(Math.pow(totalShapeWidth / 3.14, 2) / 3)
  const Ax = (totalShapeWidth / 2 - innerShapeHalfHeight) * 2
  const Cx = totalShapeWidth / 2 + innerShapeHalfHeight + leftPadding

  const Ay = totalShapeHeight / 2 + innerShapeHalfHeight / 2  - bottomPadding
  const Cy = totalShapeHeight / 2 + innerShapeHalfHeight / 2 - bottomPadding
  const arrowRight = new window.Konva.Arrow({
  x: 0,
  y: 0,
  points: [Ax, Cy, Cx, Ay],
  pointerLength: 2,
  pointerWidth: 2,
  fill: '#00000033',
  stroke: '#00000033',
  strokeWidth: 1
});
const arrowLeft = new window.Konva.Arrow({
  x: 0,
  y: 0,
  points: [Ax, Ay, Ax / 2, Cy],
  pointerLength: 2,
  pointerWidth: 2,
  fill: '#00000033',
  stroke: '#00000033',
  strokeWidth: 1
});

frameLayer.add(arrowRight);
frameLayer.add(arrowLeft);

  frameLayer.draw()


}
function drawForm(layer: Layer, layerCircle: Layer) {
  const totalShapeWidth = width - leftPadding
  const totalShapeHeight = height - bottomPadding
  let isPrintLines = false
  let isPrintCircles = false
  if (isStitches.value === 'stitches_yes') {
    isPrintLines = true
  }
  if (isButtons.value === 'buttons_yes') {
    isPrintCircles = true
  }
  if (crossTopSewing.value == 'cross_top_sewing_no') {
    isPrintLines = false
  }

  layer.removeChildren()
  layerCircle.removeChildren()

  // init inner shape box
  const shapeInnerGroup = new window.Konva.Group()

  const circlesGroup = new window.Konva.Group()

  const innerShapeHalfHeight = Math.sqrt(Math.pow(totalShapeWidth / 3.14, 2) / 3)
  const Ax = totalShapeWidth / 2 - innerShapeHalfHeight + leftPadding / 2
  const Cx = totalShapeWidth / 2 + innerShapeHalfHeight + leftPadding / 2

  const Ay = totalShapeHeight / 2 + innerShapeHalfHeight - bottomPadding / 2
  const Cy = totalShapeHeight / 2 - innerShapeHalfHeight - bottomPadding / 2
  if (isPrintLines) {
    const lineLeft = new window.Konva.Line({
      points: [Ax, Ay, Cx, Cy],
      stroke: '#00000033',
      strokeWidth: strokeWidth,
    })

    shapeInnerGroup.add(lineLeft)
    const lineRight = new window.Konva.Line({
      points: [Ax, Cy, Cx, Ay],
      stroke: '#00000033',
      strokeWidth: strokeWidth,
    })
    shapeInnerGroup.add(lineRight)
  }

  //add grid into layer
  layer.add(shapeInnerGroup)

  const layerCtx = layer.getContext()
  if (isPrintCircles) {
    const centerX = (Ax + Cx) / 2
    const centerY = (Ay + Cy) / 2
    layerCtx.clearRect(
      centerX - innerCircleRadius * 2,
      centerY - innerCircleRadius * 2,
      outerSquareWitdh,
      outerSquareWitdh,
    )

    const circle3 = new window.Konva.Circle({
      x: centerX,
      y: centerY,
      radius: innerCircleRadius + 5,
      fill: '#E4E4E4',
      strokeWidth: strokeWidth,
    })

    const circle2 = new window.Konva.Circle({
      x: centerX,
      y: centerY,
      radius: innerCircleRadius,
      stroke: '#00000033',
      fill: '#E4E4E4',
      strokeWidth: strokeWidth,
    })
    circlesGroup.add(circle3)
    circlesGroup.add(circle2)

    if (isButtonMiddle.value === 'button_middle_yes') {
      layerCircle.add(circlesGroup)
    }
  }
  layer.draw()
  layerCircle.draw()
  emit('onformdraw')
}
</script>
<template>
  <konva-stage
    id="stage"
    ref="stage"
    :config="{
      width,
      height,
    }"
  />
</template>

<style scoped></style>
